import * as z from 'zod'
import { isValidPhoneNumber } from 'libphonenumber-js'

export enum GeoMode {
  circle = 'circle',
  polygon = 'polygon',
  linestring = 'linestring',
}

// Following the zod v4 rfc5322Email regex
// Ref: https://github.com/colinhacks/zod/blob/0cdc0b8597999fd9ca99767b912c1e82c1ff2d6c/packages/zod/src/v4/core/regexes.ts#L43
const emailRegex =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i

export const GeoFenceCreateSchema = z.object({
  geoFence: z
    .object({
      title: z.string().min(1, 'Please add a title for the geofence'),
      highRisk: z.boolean(),
      address: z.string().min(1, 'Set position'),
    })
    .and(
      z
        .object({
          type: z.literal(GeoMode.circle),
          radius: z.coerce
            .number()
            .min(10, 'The minimum radius value must be 10m')
            .max(100000, 'The radius must be smaller than 100km'),
        })
        .or(
          z.object({
            type: z.literal(GeoMode.polygon),
            points: z
              .array(
                z.object({
                  lat: z
                    .number()
                    .min(-90, 'Latitude must be between -90 and 90')
                    .max(90, 'Latitude must be between -90 and 90'),
                  lng: z
                    .number()
                    .min(-180, 'Longitude must be between -180 and 180')
                    .max(180, 'Longitude must be between -180 and 180'),
                }),
              )
              .min(3, 'A polygon must have at least 3 points')
              .default([]),
          }),
        )
        .or(
          z.object({
            type: z.literal(GeoMode.linestring),
            radius: z
              .number()
              .min(10, 'Radius must be at least 10 meters')
              .max(200, 'Radius can at most be 200m')
              .default(25),
            color: z.string().optional(),
          }),
        ),
    ),
  alerts: z
    .array(
      z.object({
        title: z.string(),
        alertId: z.number().optional(),
        enabledAlert: z.boolean(),
        contacts: z.array(z.union([z.string().regex(emailRegex), z.string()])).default([]),
        alertOnEnter: z.boolean(),
        alertOnExit: z.boolean(),
        enableDwellTime: z.boolean(),
        dwellTime: z.coerce.number().nullable(),
        tags: z.array(z.object({ id: z.number(), name: z.string() })).default([]),
      }),
    )
    .superRefine((data, ctx) => {
      for (const [alertRuleIndex, alertRule] of data.entries()) {
        if (!alertRule.title) {
          ctx.addIssue({
            code: z.ZodIssueCode.too_small,
            minimum: 1,
            message: 'Alert title is required',
            path: [`${alertRuleIndex}.title`],
            type: 'string',
            inclusive: true,
            exact: true,
          })
        }

        if (alertRule.enableDwellTime) {
          if (
            !alertRule.dwellTime ||
            (alertRule.dwellTime &&
              (!Number.isFinite(alertRule.dwellTime) || alertRule.dwellTime < 1))
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.too_small,
              minimum: 1,
              message: 'Need a dwell time',
              path: [`${alertRuleIndex}.dwellTime`],
              type: 'number',
              inclusive: true,
              exact: true,
            })
          }
        }

        if (alertRule.enabledAlert && alertRule.contacts.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.too_small,
            minimum: 1,
            message: 'Select a contact or disable alerts',
            path: [`${alertRuleIndex}.contacts`],
            type: 'array',
            inclusive: true,
            exact: true,
          })
        } else if (alertRule.enabledAlert && alertRule.contacts.length > 6) {
          ctx.addIssue({
            code: z.ZodIssueCode.too_big,
            maximum: 6,
            message: 'You can add up to 6 contacts',
            path: [`${alertRuleIndex}.contacts`],
            type: 'array',
            inclusive: true,
            exact: true,
          })
        } else {
          alertRule.contacts.forEach((contact, index) => {
            // Not allowing any spacing for both email and phone number
            // Valid emails with printable characters are allowed
            // Valid international phone numbers are allowed
            let isInvalidContact = false
            if (contact.includes(' ')) {
              isInvalidContact = true
            }

            const parsedEmail = z
              .string()
              .regex(emailRegex)
              .safeParse(contact, { path: [`${alertRuleIndex}.contacts`, index] })

            if (!parsedEmail.success && !isValidPhoneNumber(contact)) {
              isInvalidContact = true
            }

            if (isInvalidContact) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Emails or Phone numbers are invalid',
                path: [`${alertRuleIndex}.contacts`, index],
              })
            }
          })
        }

        if (!alertRule.alertOnEnter && !alertRule.alertOnExit && !alertRule.enableDwellTime) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Select a condition for alert',
            path: [`${alertRuleIndex}.alertOnEnter`],
          })
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Select a condition for alert',
            path: [`${alertRuleIndex}.alertOnExit`],
          })
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Select a condition for alert',
            path: [`${alertRuleIndex}.enableDwellTime`],
          })
        }
      }
    }),
})
export type GeoFenceCreateSchemaType = z.infer<typeof GeoFenceCreateSchema>
export const GEOFENCE_FIELDS = {
  title: 'geoFence.title',
  highRisk: 'geoFence.highRisk',
  radius: 'geoFence.radius',
  address: 'geoFence.address',
  type: 'geoFence.type',
  points: 'geoFence.points',
  color: 'geoFence.color',
  sendAlerts: 'alerts.sendAlerts',
  contacts: 'alerts.contacts',
  enterGeofence: 'alerts.enterGeofence',
  exitGeofence: 'alerts.exitGeofence',
  enableDwellTime: 'alerts.enableDwellTime',
  dwellTime: 'alerts.dwellTime',
} as const
